// CORE
import { Map as ImmutableMap } from 'immutable';
import React, { useState, useMemo, useCallback } from 'react';

import { billerAccountsSelectors } from 'app-components/flux/biller-accounts';
import { billerLoginsSelectors } from 'app-components/flux/biller-logins';
import {
  scheduledTransactionsActions,
  scheduledTransactionsSelectors,
  scheduledTransactionsTypes,
  scheduledTransactionsUtils,
} from 'app-components/flux/scheduled-transactions';
import { transactionsSelectors, transactionsUtils } from 'app-components/flux/transactions';
import type { TransactionProps, TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';
import { upcomingSelectors } from 'app-components/flux/upcoming';

// MUI
import ExitIcon from '@mui/icons-material/ClearRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import StopIcon from '@mui/icons-material/Stop';
import ButtonBase from '@mui/material/ButtonBase';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { numberUtils } from 'app-components/utils/number';
import { HandleEndOption } from 'components/BillPresentment/utils';

import { useAppSelector } from 'hooks/redux';
// Data
import { useDispatch } from 'react-redux';

import BillPresentmentConnectBox from 'components/BillPresentmentConnectBox';
import ConnectedAvatar from 'components/ConnectedAvatar';
// Custom
import { DIALOG_TYPE as DIALOG_CONFIRMATION, mkConfirmationDialogProps } from 'components/Dialogs/ConfirmationDialog';
import NestedQMenu from 'components/NestedQMenu';
import QButton, { QButtonVariantEnum } from 'components/QButton';
import ScheduledTransactionEditDialog from 'components/ScheduledTransactions/ScheduledTransactionEditDialog';
import SeriesBarChart from 'components/Upcoming/seriesBarChart';
import SkeletonRow from 'components/Upcoming/skeleton';
import ViewRow from 'components/Upcoming/viewRow';
import { getModelHistory } from 'containers/Upcoming/selectors';
import AlertDialog from '../AlertDialog';

const useStyles = makeStyles()((theme) => ({
  root: {
    minHeight: 284,
  },
  dialogBackground: {
    background: theme.palette.background.paper,
  },
  canceledChip: {
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.layer1,
    padding: '2px 4px',
    borderRadius: 6,
  },

  // header + title stuff
  titleRow: {
    top: 0,
    flexShrink: 0,
    position: 'sticky',
    padding: '24px 24px 24px',
    marginBottom: 4,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    backgroundColor: 'inherit',
    borderBottom: `1px solid ${theme.palette.border.light}`,
    zIndex: 1,
    '&::after': {
      content: "''",
      position: 'absolute',
      bottom: -8,
      left: 0,
      height: 8,
      width: '100%',
      backgroundImage: 'unset',
    },
  },
  nameColumn: {
    marginLeft: 24,
    flexGrow: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  buttonRow: {
    flexShrink: 0,
    display: 'flex',
  },
  exitButton: {
    height: 34,
    width: 34,
    padding: 5,
    borderRadius: 17,
    '&:hover': {
      backgroundColor: theme.applyOpacityToHex?.(theme.palette.backdrop.dark, 0.5),
    },
  },

  // graph
  graphSection: {
    margin: '16px 12px',
    height: 208,
    flexShrink: 0,
  },

  centeredRow: {
    padding: '16px 24px',
    flexShrink: 0,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  comingUp: {
    padding: 24,
    borderRadius: 12,
    margin: '0 16px',
    backgroundColor: theme.palette.background.default,
  },

  lowerSection: {
    width: '100%',
    padding: '32px 24px 24px',
  },

  zeroState: {
    margin: '36px 48px',
  },
}));

const location = 'view series';

const ViewSeriesDialog = (props: { txn?: TransactionProps; dialogId?: string }) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { stModelId } = props.txn || {};

  const model = useAppSelector((state) =>
    scheduledTransactionsSelectors.getScheduledTransactionById(state, stModelId || ''),
  );
  const transaction = model?.transaction || props.txn || ({} as TransactionProps);
  const { history, txn } = useAppSelector((state) => getModelHistory(state, model)) as {
    history: ImmutableMap<string, TransactionRecord>;
    txn: TransactionProps;
  };

  const displayName = scheduledTransactionsUtils?.getDisplayName?.(transaction) || '';

  const isIncome = useAppSelector((state) => transactionsSelectors.isIncomeTxn(state, transaction));
  let color = isIncome ? theme.palette.legend.income : theme.palette.legend.bill;
  if (model?.isCompleted) color = theme.palette.text.secondary;
  const { classes } = useStyles();

  const billerAccount = useAppSelector((state) =>
    billerAccountsSelectors.getBillerAccountForId(state, model?.billPresentmentAccountId),
  );
  const billerLogin = useAppSelector((state) =>
    billerLoginsSelectors.getBillerLoginForId(state, billerAccount?.billerLoginID),
  );

  const negator =
    (transactionsUtils.isTransferTxn(transaction) && Math.sign(transaction.amount)) || (isIncome ? 1 : -1);
  const { chartData, average, sum } = upcomingSelectors.generateChartData(history, negator);

  const historyDisplay = history
    .filter((historyTxn) => historyTxn.source !== 'SCHEDULED_TRANSACTION_PENDING')
    .map((historyTxn) => <ViewRow key={historyTxn.id} txn={historyTxn} isIncome={isIncome} past />)
    .toIndexedSeq();

  const frequency = model && scheduledTransactionsUtils.frequencyAliasFromRecurrence(model.recurrence);

  // +++     State values     +++
  const [editSeries, setEditSeries] = useState<boolean | number>(false);
  const [openState, setOpenState] = useState(true);
  const [dirtyDialog, setDirtyDialog] = useState(false);
  const [optionsMenuAnchor, setOptionsMenuAnchor] = useState(null);
  const [endSeries, setEndSeries] = useState(false);
  const [deleteSeries, setDeleteSeries] = useState(false);

  const handleOptionsMenuToggle = useCallback(
    (e?: any) => {
      if (!optionsMenuAnchor && e) setOptionsMenuAnchor(e.currentTarget);
      else setOptionsMenuAnchor(null);
    },
    [optionsMenuAnchor],
  );

  const handleOptionsMenuSelect = useCallback(
    (callback: any) => {
      setOptionsMenuAnchor(null);
      if (typeof callback === 'function') {
        callback();
      }
    },
    [setOptionsMenuAnchor],
  );

  const onClose = useCallback(() => {
    setOpenState(false);
    dispatch(rootUiActions.removeDialog(props.dialogId));
  }, [setOpenState, dispatch, props.dialogId]);

  const openEditSeries = useCallback((tab?: number) => setEditSeries(tab || true), [setEditSeries]);
  const closeEditDialog = useCallback(
    (isDirty?: boolean, forceClose?: boolean) => {
      if (forceClose) {
        setEditSeries(false);
        onClose();
      }
      if (isDirty) {
        setDirtyDialog(true);
      } else {
        setEditSeries(false);
      }
    },
    [onClose],
  );

  const openEndSeries = useCallback(() => {
    setEndSeries(true);
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'end-series-confirmation',
          type: DIALOG_CONFIRMATION,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              title: 'End series',
              content: (
                <Typography variant='bodyRegular' id='confirmation-dialog-description'>
                  Ending your&nbsp;<b>{displayName || 'this transaction'}</b>&nbsp;series will only mark it as ended. It
                  will not delete your series or any history related to it.
                </Typography>
              ),
              confirmLabel: 'End Series',
              allowNesting: true,
              confirmAction: (_reason, _closeEvent) => {
                HandleEndOption(dispatch, model, billerLogin, location, true);
                /*  dispatch(updateScheduledTransaction(scheduledTransactionsTypes.mkScheduledTransaction({
            id: stModelId,
            isCompleted: true,
          }))); */
                setEndSeries(false);
              },
              denyLabel: 'Cancel',
              denyAction: (_reason, _closeEvent) => {
                setEndSeries(false);
              },
            }),
          ),
        }),
      ),
    );
  }, [dispatch, displayName, model, billerLogin]);

  const openDeleteSeries = useCallback(() => {
    setDeleteSeries(true);
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'delete-series-confirmation',
          type: DIALOG_CONFIRMATION,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              title: 'Delete recurring series',
              content: `Deleting the recurring series for ${displayName} will delete all future reminders for this series.`,
              confirmLabel: 'Delete',
              confirmButtonVariant: QButtonVariantEnum.DELETE,
              allowNesting: true,
              confirmAction: (_reason, _closeEvent) => {
                if (stModelId) {
                  dispatch(
                    scheduledTransactionsActions.deleteScheduledTransaction(
                      scheduledTransactionsTypes.mkScheduledTransaction({
                        id: stModelId,
                        isUserVerified: true,
                        isDeleted: true,
                      }),
                      { undo: { userMessage: 'Recurring transaction deleted.' } },
                    ),
                  );
                }
                setDeleteSeries(false);
                closeEditDialog(false, true);
              },
              denyLabel: 'Cancel',
              denyButtonVariant: QButtonVariantEnum.DEFAULT,
              denyAction: (_reason, _closeEvent) => {
                setDeleteSeries(false);
              },
            }),
          ),
        }),
      ),
    );
  }, [dispatch, stModelId, setDeleteSeries, displayName, closeEditDialog]);

  const optionsMenuItems = useMemo(
    () =>
      model?.isCompleted
        ? [{ label: 'Delete series', value: openDeleteSeries }]
        : [
            { label: 'Edit series', value: openEditSeries, startIcon: <EditIcon /> },
            { label: 'Delete series', value: openDeleteSeries, startIcon: <DeleteOutlineIcon />, divider: true },
            {
              label: 'End series',
              value: openEndSeries,
              startIcon: <StopIcon />,
              subtext: 'Completed or Cancelled service.',
            },
          ],
    [openEndSeries, openEditSeries, openDeleteSeries, model],
  );

  return (
    <>
      <Dialog
        open={openState && !editSeries && !endSeries && !deleteSeries}
        maxWidth='md'
        fullWidth
        classes={{ paper: classes.root }}
        onClose={onClose}
      >
        <div className={classes.dialogBackground}>
          <div sharedcomponentid='ViewSeriesDialog' className={classes.titleRow}>
            <ConnectedAvatar model={model} txn={transaction} size={80} iconSize={32} color={color} />
            <div className={classes.nameColumn}>
              <Typography
                id='series-modal-title-text'
                variant='displayRegular'
                style={{ width: '100%', padding: '8px 0' }}
                noWrap
              >
                {displayName}
              </Typography>
              {model?.isCompleted ? (
                <Typography variant='paragraphRegular' className={classes.canceledChip}>
                  Canceled
                </Typography>
              ) : (
                <Typography variant='bodyMedium' color={theme.palette.text.secondary}>
                  {frequency}
                </Typography>
              )}
            </div>
            <div className={classes.buttonRow}>
              {model && (
                <QButton id='series-options-btn' onClick={handleOptionsMenuToggle}>
                  options
                </QButton>
              )}
              <NestedQMenu
                anchorEl={optionsMenuAnchor}
                name='series-options-menu'
                options={optionsMenuItems}
                onChange={handleOptionsMenuSelect}
                open={Boolean(optionsMenuAnchor)}
                onClose={handleOptionsMenuToggle}
              />
              <ButtonBase id='dlg-close' className={classes.exitButton} onClick={onClose} focusRipple>
                <ExitIcon />
              </ButtonBase>
            </div>
          </div>

          {!model?.isCompleted && (
            <BillPresentmentConnectBox model={model} location={location} onConnect={onClose} onEdit={openEditSeries} />
          )}

          {model ? (
            <>
              <div className={classes.graphSection}>
                <SeriesBarChart
                  colorOverride={model.isCompleted ? color : undefined}
                  chartData={chartData}
                  average={isIncome ? average : average * -1}
                  isIncome={isIncome}
                />
              </div>

              <div className={classes.centeredRow}>
                <Typography variant='bodyRegular'>
                  {`Total in Last 6 months : ${numberUtils.formatNumber(
                    sum,
                    undefined,
                    '0,0',
                    numberUtils.ShowSignEnum.POSITIVE_ONLY,
                  )}`}
                </Typography>
              </div>

              {!model.isCompleted && (
                <div className={classes.comingUp}>
                  <Typography variant='titleMedium' style={{ paddingBottom: 8 }}>
                    Coming Up
                  </Typography>
                  {txn.type ? (
                    <ViewRow txn={txn as TransactionRecord} isIncome={isIncome} baseId='coming-soon' />
                  ) : (
                    <SkeletonRow string2="You don't have anything coming up." />
                  )}
                </div>
              )}

              <div className={classes.lowerSection}>
                <Typography variant='titleMedium'>History</Typography>

                {historyDisplay?.size ? (
                  historyDisplay
                ) : (
                  <SkeletonRow string1='Blank Slate!' string2='your past is clear' />
                )}
              </div>
            </>
          ) : (
            <div className={classes.zeroState}>
              <SkeletonRow
                string1='One Time Bill!'
                string2="there's no recurring model associated with this transaction"
              />
            </div>
          )}
        </div>
      </Dialog>

      {editSeries && (
        <ScheduledTransactionEditDialog
          open={Boolean(model)}
          key={model ? model.clientId || model.id : 'noKey'}
          scheduledTransaction={model}
          onClose={closeEditDialog}
          initialTab={typeof editSeries === 'number' ? editSeries : 0}
        />
      )}

      {dirtyDialog && (
        <AlertDialog
          dialogId={'ignore this warning'}
          title='Just checking...'
          content='Are you sure you want to discard all changes?'
          onClose={(btnObj: any) => {
            if (btnObj.btnPressed === 'Discard') setEditSeries(false);
            setDirtyDialog(false);
          }}
          buttons={['Go Back', 'Discard']}
        />
      )}
    </>
  );
};

ViewSeriesDialog.displayName = 'ViewSeriesDialog';

export default ViewSeriesDialog;
